"use client"

import { useEffect, useRef } from "react"
import { Box, Flex, Text } from "@chakra-ui/react"

import echarts from "../../sales/echarts/lib"
import { ChartPanel } from "../../../components/shared/Section"
import { neutral, brand, type, radius, motion } from "../../../theme/tokens"
import { fmtShort, fmtR, fmtPct } from "../format"

const PAIRS = [
  { key: "cur", label: "Current month" },
  { key: "prev", label: "Previous month" },
]

/**
 * Sales at cost per rep, this year beside last year, for one of the two month pairs.
 * Bars sorted biggest-TY first; the selected rep's label is emphasised.
 */
const RepChart = ({ summary, pair, onPairChange, selectedRep }) => {
  const containerRef = useRef(null)
  const chartRef = useRef(null)
  const L = summary?.period?.labels || {}
  const lyKey = pair === "prev" ? "prev_ly" : "cur_ly"
  const tyKey = pair === "prev" ? "prev_ty" : "cur_ty"
  const yoyKey = pair === "prev" ? "prev_yoy" : "cur_yoy"
  const lyLabel = pair === "prev" ? L.prevLY : L.curLY
  const tyLabel = pair === "prev" ? L.prev : L.cur

  useEffect(() => {
    if (!containerRef.current || !summary) return undefined
    const lines = [...(summary.reps || []), ...(summary.unassigned ? [summary.unassigned] : [])]
      .map((r) => ({ rep: r.rep === "Unassigned" ? "Not assigned" : r.rep, ly: r[lyKey] || 0, ty: r[tyKey] || 0, yoy: r[yoyKey] }))
      .sort((a, b) => a.ty - b.ty) // horizontal bars draw bottom-up; biggest on top
    const chart = chartRef.current || echarts.init(containerRef.current)
    chartRef.current = chart
    chart.setOption(
      {
        grid: { left: 8, right: 24, top: 28, bottom: 4, containLabel: true },
        legend: { top: 0, right: 0, itemWidth: 10, itemHeight: 10, textStyle: { color: neutral[600], fontSize: 11 } },
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow", shadowStyle: { color: "rgba(14,124,134,0.06)" } },
          confine: true,
          backgroundColor: neutral[0],
          borderColor: neutral[200],
          borderWidth: 1,
          padding: [8, 12],
          textStyle: { color: neutral[800], fontSize: 12 },
          extraCssText: "box-shadow: 0 6px 18px rgba(29,47,58,0.14); border-radius: 8px;",
          formatter: (params) => {
            const i = params[0]?.dataIndex
            const d = lines[i]
            if (!d) return ""
            const yoy = d.yoy == null ? "" : `<br/><span style="color:${d.yoy >= 0 ? "#12775F" : "#BF3F38"}">${d.yoy >= 0 ? "▲" : "▼"} ${fmtPct(d.yoy)} YoY</span>`
            return `<b>${d.rep}</b><br/>${tyLabel}: <b>${fmtR(d.ty)}</b><br/>${lyLabel}: ${fmtR(d.ly)}${yoy}`
          },
        },
        xAxis: {
          type: "value",
          axisLabel: { color: neutral[500], fontSize: 10.5, formatter: fmtShort },
          splitLine: { lineStyle: { color: "rgba(229,231,235,0.6)", type: [3, 3] } },
        },
        yAxis: {
          type: "category",
          data: lines.map((d) => d.rep),
          axisTick: { show: false },
          axisLine: { lineStyle: { color: neutral[200] } },
          axisLabel: {
            color: neutral[600],
            fontSize: 11,
            formatter: (v) => (v === selectedRep ? `{sel|${v}}` : v),
            rich: { sel: { color: brand[600], fontWeight: 600, fontSize: 11 } },
          },
        },
        series: [
          {
            name: lyLabel,
            type: "bar",
            data: lines.map((d) => d.ly),
            barGap: "10%",
            barCategoryGap: "35%",
            itemStyle: { color: neutral[300], borderRadius: [0, 3, 3, 0] },
          },
          {
            name: tyLabel,
            type: "bar",
            data: lines.map((d) => d.ty),
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                { offset: 0, color: brand[500] },
                { offset: 1, color: brand[300] },
              ]),
              borderRadius: [0, 3, 3, 0],
            },
          },
        ],
      },
      true,
    )
    const onResize = () => chart.resize()
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [summary, pair, selectedRep, lyKey, tyKey, yoyKey, lyLabel, tyLabel])

  useEffect(() => () => { chartRef.current?.dispose(); chartRef.current = null }, [])

  const n = (summary?.reps?.length || 0) + (summary?.unassigned ? 1 : 0)
  const height = `${Math.max(240, 40 + n * 26)}px`

  return (
    <ChartPanel
      title="Sales at cost by rep"
      height={height}
      hint={
        <Flex borderRadius={radius.sm} border="1px solid" borderColor={neutral[200]} overflow="hidden" role="group" aria-label="Month pair">
          {PAIRS.map((p) => {
            const active = p.key === pair
            return (
              <Box
                key={p.key}
                as="button"
                type="button"
                onClick={() => onPairChange(p.key)}
                px={2.5}
                py={0.5}
                fontSize={type.size.caption}
                fontWeight={type.weight.semibold}
                bg={active ? brand[500] : neutral[0]}
                color={active ? neutral[0] : neutral[600]}
                _hover={active ? {} : { bg: neutral[100] }}
                transition={`background ${motion.fast}`}
                aria-pressed={active}
              >
                {p.label}
              </Box>
            )
          })}
        </Flex>
      }
    >
      {summary ? (
        <Box ref={containerRef} position="absolute" inset={0} />
      ) : (
        <Text fontSize={type.size.small} color={neutral[400]}>Loading…</Text>
      )}
    </ChartPanel>
  )
}

export default RepChart
